import { Step, Tour } from 'nextstepjs'

/** Matches `data-nextstep` on the main nav Workouts target; shared so step index stays in sync. */
export const APP_TOUR_NAV_WORKOUTS_SELECTOR = '[data-nextstep="nav-workouts"]' as const

/** Matches `data-nextstep` on the Home featured-workouts shelf (`FeaturedWorkoutsSection`). */
export const APP_TOUR_FEATURED_WORKOUTS_SELECTOR = '[data-nextstep="featured-workouts"]' as const

export const APP_TOUR_ID = 'grndhouse-app-tour' as const

const featuredWorkoutsStep: Step = {
  icon: null,
  title: 'Featured Workouts',
  content: 'Free workouts, no subscription needed. Try one to get a feel for the app.',
  selector: APP_TOUR_FEATURED_WORKOUTS_SELECTOR,
  side: 'bottom-left',
  showControls: true,
  showSkip: false,
  disableInteraction: true,
}

const suggestedSeriesStep: Step = {
  icon: null,
  title: 'Suggested Series',
  content: 'Series curated just for you.Start here and build momentum',
  selector: '[data-nextstep="suggested-series"]',
  side: 'top-left',
  showControls: true,
  showSkip: false,
  disableInteraction: true,
}

const suggestedWorkoutsStep: Step = {
  icon: null,
  title: 'Suggested Workouts',
  content: 'Suggested workouts handpicked for you. Just pick one and go.',
  selector: '[data-nextstep="suggested-workouts"]',
  side: 'bottom-left',
  showControls: true,
  showSkip: false,
  disableInteraction: true,
}

const workoutsNavStep: Step = {
  icon: null,
  title: 'Workouts',
  content: 'Explore a vast library of workouts. Find your next challenge.',
  selector: APP_TOUR_NAV_WORKOUTS_SELECTOR,
  side: 'top',
  showControls: true,
  showSkip: false,
  disableInteraction: true,
  pointerPadding: 8,
  // Real navigation (Home -> Profile). See the module doc comment above `buildAppTourTours`
  // for why `nextRoute`/`prevRoute` are only set here and on `membershipStep.prevRoute` —
  // every other transition stays on the same page and must leave them unset.
  nextRoute: '/profile/personal',
}

const membershipStep: Step = {
  icon: null,
  title: 'Membership',
  content: 'This is the place where you will manage your subscription after trial period.',
  selector: '[data-nextstep="membership"]',
  side: 'bottom-left',
  showControls: true,
  showSkip: false,
  disableInteraction: true,
  // Real navigation (Profile -> Home) going backward, mirroring workoutsNavStep.nextRoute.
  prevRoute: '/',
}

const personalDetailsStep: Step = {
  icon: null,
  title: 'Personal Details',
  content: 'Add your metrics/username here to personalize your experience.',
  selector: '[data-nextstep="personal-details"]',
  side: 'right-top',
  showControls: true,
  showSkip: false,
  pointerPadding: 40,
  disableInteraction: true,
}

/**
 * Builds the app tour for this session. `includeFeaturedWorkouts` decides whether the
 * Featured Workouts step is prepended — that shelf only renders for accounts without full
 * catalog access (and only when the environment has a non-empty featured set), so a step
 * targeting it must be left out entirely for everyone else. See `use-app-tour-steps.ts`,
 * the single place that resolves the answer before this is called.
 *
 * **Only set `nextRoute`/`prevRoute` on a step when the adjacent step is genuinely on a
 * different page** (`workoutsNavStep.nextRoute` and `membershipStep.prevRoute` — the only
 * two). nextstepjs's `nextStep`/`prevStep` branch on whether the *departing* step declares
 * one: if it does, they call `router.push(route)` and defer advancing to a `MutationObserver`
 * that waits for the next step's selector to be *added* to the DOM, instead of the plain
 * `setCurrentStep` + `scrollIntoView` every route-less transition uses. Pointing a same-page
 * transition at the current route (e.g. `nextRoute: '/'` while already on `/`) triggers that
 * branch for no reason — no navigation happens, so the target is never freshly inserted, and
 * if it doesn't already exist yet (a step whose section is still loading, or one that never
 * renders for this account, e.g. no suggested series) the observer waits forever and the tour
 * gets stuck re-running the same step (GH-935). Confirmed by removing the featured/suggested/
 * workouts steps' self-referencing `'/'` routes fixed exactly that freeze.
 */
export function buildAppTourTours(includeFeaturedWorkouts: boolean): Tour[] {
  return [
    {
      tour: APP_TOUR_ID,
      steps: [
        ...(includeFeaturedWorkouts ? [featuredWorkoutsStep] : []),
        suggestedSeriesStep,
        suggestedWorkoutsStep,
        workoutsNavStep,
        membershipStep,
        personalDetailsStep,
      ],
    },
  ]
}

/**
 * 0-based index of the Workouts nav step, derived from step order in the given tours —
 * that order shifts by one whenever the Featured Workouts step is or isn't included, so this
 * must be recomputed per session rather than kept as a module-level constant.
 */
export function getAppTourNavWorkoutsStepIndex(tours: Tour[]): number {
  const firstTour = tours[0]
  if (!firstTour) {
    throw new Error('appTourSteps: at least one tour is required')
  }

  const index = firstTour.steps.findIndex((s) => s.selector === APP_TOUR_NAV_WORKOUTS_SELECTOR)

  if (index < 0) {
    throw new Error('appTourSteps: a step targeting main nav Workouts must exist')
  }

  return index
}
